import React, { useState, useCallback, useRef, useEffect } from 'react';
import { ListItem, IconButton, Box, TextField, TableRow, TableCell } from '@mui/material';
import { useDispatch } from 'react-redux';
import { renameWatchlistSectionSuccess, Watchlist, WatchlistItemData } from '../../reducer/watchList.reducer';
import { Watchlist_Type, Watchlist_WatchlistItem_Type } from '../../proto/generated/watchlist';
import BinIcon from '../../assets/icons/BinIcon';
import { renameWatchlistSection } from '../../services/apis/watchlist';
import { updateWatchlist } from './WatchUtils';
import WatchListNormalView from './WatchListNormalView';
import { selectCustomizedColumns, selectNumberOfCustomizedWatchlistColumns, selectUserDetails, watchListView } from '../../selectors/user';
import { useSelector } from 'react-redux';
import WatchlistTableView from './WatchlistTableView';
import { primary as primaryColor } from '../../assets/colors'
import ThinArrowRightIcon from '../../assets/icons/ThinArrowRightIcon';
import ThinArrowDownIcon from '../../assets/icons/ThinArrowDownIcon';
import { useShouldAllowAccess } from '../../hooks/useWatchlistAccess';
import { useAdminScreenerAccess } from '../../hooks/useAdminScreenerAccess';
interface Props {
    selectedWatchlistId: string;
    draggedOverItem: WatchlistItemData | null;
    data: WatchlistItemData[];
    handleWatchlistItemAddOrDelete: (item: WatchlistItemData[], add: boolean, watchlistId: string) => void;
    subscribeOrderBook: (security: any, subscribe: boolean) => void;
    handleDragOver: Function;
    handleDragEnd: React.DragEventHandler<HTMLLIElement>;
    handleDragStart: Function;
    onItemClick: (e: React.MouseEvent, id: number) => void;
    onItemActivate?: (item: WatchlistItemData) => void;
    selectedItemIds?: Set<string>;
    totalItems?: number;
    lastIndexItem?: boolean
    editMode: boolean;
    selectedWatchlist: Watchlist;
    onClearFocus?: () => void;
}

const WatchListGroupedPanel: React.FC<Props> = ({
    selectedWatchlistId,
    draggedOverItem,
    data,
    handleWatchlistItemAddOrDelete,
    subscribeOrderBook,
    handleDragOver,
    handleDragEnd,
    handleDragStart,
    onItemClick,
    onItemActivate,
    selectedItemIds,
    totalItems,
    lastIndexItem,
    editMode,
    selectedWatchlist,
    onClearFocus
}) => {
    const dispatch = useDispatch()
    const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
    const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
    const [editedSectionName, setEditedSectionName] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(true)
    const dataUpdating = useRef<boolean>(false)
    const spanRef = useRef<HTMLSpanElement>(null);
    const [inputWidth, setInputWidth] = useState(100)
    const user = useSelector(selectUserDetails);
    const numberOfTableColumns = useSelector(selectNumberOfCustomizedWatchlistColumns);
    const shouldAllowAccess = useShouldAllowAccess();
    const shouldAllowAdminAccess = useAdminScreenerAccess();
    const toggleSection = useCallback((id: string) => {
        setCollapsedSections(prev => {
            const newSet = new Set(prev);
            newSet.has(id) ? newSet.delete(id) : newSet.add(id);
            return newSet;
        });
    }, []);
    const rectCache = useRef<DOMRect | null>(null)
    const getSectionWithChildren = useCallback((sectionItem: WatchlistItemData): WatchlistItemData[] => {
        const startIndex = data.findIndex(d => d.id === sectionItem.id);
        const result: WatchlistItemData[] = [];
        for (let i = startIndex; i < data.length; i++) {
            const currentItem = data[i];
            if (i !== startIndex && currentItem.item.type === Watchlist_WatchlistItem_Type.SECTION) {
                break;
            }
            result.push(currentItem);
        }
        return result;
    }, [data]);
    const tableView = useSelector(watchListView);
    const custom = useSelector(selectCustomizedColumns)

    const onRenameds = async (newName: string, watchlistId: string, sectionName: string) => {
        try {
            await renameWatchlistSection({
                watchlistId,
                sectionName,
                name: newName,
            });

            await updateWatchlist(dispatch, selectedWatchlistId, dataUpdating, setLoading, false, true, false);
        } catch (error) {
            console.error('Rename failed:', error);
        }
    };
    const handleSaveSectionName = async (item: WatchlistItemData) => {
        let trimmedName = editedSectionName.trim();
        if (!trimmedName) {
            setEditingSectionId(null);
            return;
        }
        const existingNames = new Set(
            data
                .filter(d => d.item.type === Watchlist_WatchlistItem_Type.SECTION)
                .map(d => d.item.sectionName?.trim())
                .filter(name => name && name !== item.item.sectionName)
        );

        if (existingNames.has(trimmedName)) {
            let suffix = 1;
            let uniqueName = `${trimmedName} (${suffix})`;
            while (existingNames.has(uniqueName)) {
                suffix++;
                uniqueName = `${trimmedName} (${suffix})`;
            }
            trimmedName = uniqueName;
        }

        try {
            await onRenameds(trimmedName, selectedWatchlistId, item.item.sectionName || '');
            dispatch(renameWatchlistSectionSuccess({
                watchlistId: selectedWatchlistId,
                sectionId: item.id,
                newName: trimmedName,
            }));
        } catch (err) {
            console.error('Rename failed:', err);
        }

        setEditingSectionId(null);
    };
    let isVisible = true;
    let currentFocusableIndex = -1;
    const totalSections = data.filter(item => item.item.type === Watchlist_WatchlistItem_Type.SECTION);
    const shouldHideDefaultSection = totalSections.length === 1 && totalSections[0].item.sectionName?.toLowerCase().includes('default');

    useEffect(() => {
        if (spanRef.current) {
            setInputWidth(spanRef.current.offsetWidth + 12);
        }
    }, [editedSectionName]);

    return (
        <>
            {data.map((item, index) => {
                const isSection = item.item.type === Watchlist_WatchlistItem_Type.SECTION;
                const itemId = item.id || `item-${index}`;
                if (isSection) {
                    if (shouldHideDefaultSection) return null;
                    if (shouldHideDefaultSection) {
                        const children = getSectionWithChildren(item);
                        const hasSecurity = children.some(child => child?.item.type !== Watchlist_WatchlistItem_Type.SECTION);
                        if (!hasSecurity) return null
                    }
                    isVisible = !collapsedSections.has(itemId);
                } else if (!isVisible) {
                    return null;
                }
                if (isSection) {
                    if (tableView) {
                        return (
                            <React.Fragment key={itemId}>
                                {draggedOverItem?.item.type === Watchlist_WatchlistItem_Type.SECTION &&
                                    draggedOverItem?.id === item.id && (
                                        <TableRow sx={{ height: '1px', boxSizing: 'border-box' }}>
                                            <TableCell colSpan={numberOfTableColumns} padding="none" sx={{ borderBottom: 'none', p: 0 }}>
                                                <Box sx={{ backgroundColor: primaryColor, height: '1px', width: '100%' }} />
                                            </TableCell>
                                        </TableRow>
                                    )}
                                <TableRow key={itemId} sx={{
                                    backgroundColor: 'background.default',
                                    ...((editMode || !selectedWatchlist?.type.includes(Watchlist_Type.REFERENCE)) && {
                                        '&:hover .delete-icon': {
                                            visibility: 'visible',
                                        }
                                    }),
                                }}
                                    draggable
                                    onDragStart={(e: any) => handleDragStart(e, item)}
                                    onDragEnter={(e) => {
                                        rectCache.current = e.currentTarget.getBoundingClientRect()
                                    }}
                                    onDragOver={(e) => {
                                        if (!rectCache) return;
                                        const centerY = (rectCache.current?.top ?? 0) + (rectCache.current?.height ?? 0) / 2
                                        if (e.clientY < centerY) {
                                            handleDragOver(e, index)
                                        } else {
                                            handleDragOver(e, index + 1)
                                        }
                                        e.preventDefault()
                                    }}
                                    onDragEnd={(e: any) => handleDragEnd(e)}

                                >
                                    <TableCell colSpan={numberOfTableColumns} sx={{ py: 0.5, padding: "0 !important", border: 'none' }}>
                                        <Box display="flex" alignItems="center" justifyContent="space-between">
                                            <Box display="flex" alignItems="center">
                                                <IconButton onClick={() => toggleSection(itemId)} size="small">
                                                    {collapsedSections.has(itemId) ? <ThinArrowRightIcon /> : <ThinArrowDownIcon />}
                                                </IconButton>

                                                {editingSectionId === itemId ? (
                                                    <>
                                                        <TextField
                                                            value={editedSectionName}
                                                            onChange={(e) => setEditedSectionName(e.target.value)}
                                                            onBlur={() => handleSaveSectionName(item)}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') handleSaveSectionName(item);
                                                            }}
                                                            autoFocus
                                                            size="small"
                                                            variant="outlined"
                                                            sx={{
                                                                ml: '4px',
                                                                width: `${inputWidth}px`,
                                                                minWidth: '100px',
                                                                '& .MuiOutlinedInput-root': {
                                                                    padding: '0px',
                                                                    height: '20px',
                                                                    fontSize: '0.75rem'
                                                                },
                                                                '& .MuiInputBase-input': {
                                                                    px: '4px',
                                                                    py: 'auto'
                                                                },
                                                                input: {
                                                                    fontSize: 14,
                                                                }
                                                            }}
                                                        /><span
                                                            ref={spanRef}
                                                            style={{
                                                                position: 'absolute',
                                                                visibility: 'hidden',
                                                                whiteSpace: 'pre',
                                                                fontSize: '14px',
                                                                fontFamily: 'Roboto, sans-serif',
                                                                padding: '0px',
                                                            }}
                                                        >
                                                            {editedSectionName || 'Section'}
                                                        </span>
                                                    </>
                                                ) : (
                                                    <Box
                                                        onDoubleClick={() => {
                                                            setEditingSectionId(itemId);
                                                            setEditedSectionName(item.item.sectionName || 'Untitled Section');
                                                        }}
                                                        sx={{ fontSize: 14, cursor: 'pointer', ml: '8px' }}
                                                    >
                                                        {item.item.sectionName || 'Untitled Section'}
                                                    </Box>
                                                )}
                                            </Box>

                                            <IconButton
                                                size="small"
                                                className="delete-icon"
                                                sx={{ visibility: 'hidden' }}

                                                onClick={() => {
                                                    const sectionWithChildren = getSectionWithChildren(item);
                                                    const sectionId = item.id ?? '';
                                                    const isCollapsed = collapsedSections.has(sectionId);
                                                    const itemsToDelete = isCollapsed
                                                        ? sectionWithChildren
                                                        : [item];

                                                    handleWatchlistItemAddOrDelete(itemsToDelete, false, selectedWatchlistId);
                                                }}

                                            >
                                                <BinIcon fontSize="small" sx={{ marginRight: custom.volume ? '10px' : '0px' }} />
                                            </IconButton>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            </React.Fragment>
                        );

                    }

                    return (
                        <Box key={itemId}>
                            <ListItem
                                draggable
                                onDragStart={(e) => handleDragStart(e, item)}
                                onDragEnter={(e) => {
                                    rectCache.current = e.currentTarget.getBoundingClientRect()
                                }}
                                onDragOver={(e) => {
                                    if (!rectCache) return;
                                    const centerY = (rectCache.current?.top ?? 0) + (rectCache.current?.height ?? 0) / 2
                                    if (e.clientY < centerY) {
                                        handleDragOver(e, index)
                                    } else {
                                        handleDragOver(e, index + 1)
                                    }
                                    e.preventDefault()
                                }}
                                onDragEnd={handleDragEnd}
                                disablePadding
                                sx={{
                                    px: 1,
                                    ...((editMode || !selectedWatchlist?.type.includes(Watchlist_Type.REFERENCE)) && {
                                        '&:hover .delete-icon': {
                                            visibility: 'visible',
                                        }
                                    }),
                                    ...((draggedOverItem?.id === item.id) ? {
                                        borderTop: 1,
                                        borderTopColor: primaryColor
                                    } : {}),
                                    backgroundColor: theme => theme.palette.background.default
                                }}
                            >
                                <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                                    <Box display="flex" alignItems="center" flex="1">
                                        <IconButton onClick={() => toggleSection(itemId)} size="small">
                                            {collapsedSections.has(itemId) ? <ThinArrowRightIcon /> : <ThinArrowDownIcon />}
                                        </IconButton>

                                        {editingSectionId === itemId ? (
                                            <>
                                                <TextField
                                                    value={editedSectionName}
                                                    onChange={(e) => setEditedSectionName(e.target.value)}
                                                    onBlur={() => handleSaveSectionName(item)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') handleSaveSectionName(item);
                                                    }}
                                                    autoFocus
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{
                                                        ml: '4px',
                                                        width: `${inputWidth}px`,
                                                        minWidth: '100px',
                                                        '& .MuiOutlinedInput-root': {
                                                            padding: '0px',
                                                            height: '20px',
                                                            fontSize: '0.75rem',
                                                        },
                                                        '& .MuiInputBase-input': {
                                                            px: '4px',
                                                            py: 'auto'
                                                        },
                                                        input: {
                                                            fontSize: 14,
                                                        },
                                                    }}
                                                /><span
                                                    ref={spanRef}
                                                    style={{
                                                        position: 'absolute',
                                                        visibility: 'hidden',
                                                        whiteSpace: 'pre',
                                                        fontSize: '14px',
                                                        fontFamily: 'Roboto, sans-serif',
                                                        padding: '0px',
                                                    }}
                                                >
                                                    {editedSectionName || 'Section'}
                                                </span>
                                            </>

                                        ) : (
                                            <Box
                                                onDoubleClick={() => {
                                                    setEditingSectionId(itemId);
                                                    setEditedSectionName(item.item.sectionName || 'Untitled Section');
                                                }}
                                                sx={{ fontSize: 14, cursor: 'pointer', ml: '8px' }}
                                            >
                                                {item.item.sectionName || 'Untitled Section'}
                                            </Box>
                                        )}
                                    </Box>

                                    {shouldAllowAccess(selectedWatchlist , editMode) && shouldAllowAdminAccess(selectedWatchlist?.shareId) && <IconButton
                                        onClick={() => {
                                            const sectionWithChildren = getSectionWithChildren(item);
                                            const sectionId = item.id ?? '';
                                            const isCollapsed = collapsedSections.has(sectionId);
                                            const itemsToDelete = isCollapsed
                                                ? sectionWithChildren
                                                : [item];

                                            handleWatchlistItemAddOrDelete(itemsToDelete, false, selectedWatchlistId);
                                        }}
                                        size="small"
                                        className="delete-icon"
                                        sx={{ visibility: 'hidden' }}
                                    >
                                        <BinIcon fontSize="small" />
                                    </IconButton>}
                                </Box>
                            </ListItem>
                        </Box>
                    );

                }
                currentFocusableIndex++;
                return tableView ? (

                    <WatchlistTableView
                        key={itemId}
                        data={item}
                        index={index}
                        isDraggedOver={draggedOverItem?.id === item.id}
                        watchlistId={selectedWatchlistId}
                        addOrRemoveWatchlistDataItem={handleWatchlistItemAddOrDelete}
                        subscribeSecurity={subscribeOrderBook}
                        handleDragOver={handleDragOver}
                        handleDragEnd={handleDragEnd}
                        handleDragStart={handleDragStart}
                        selectedItemIds={selectedItemIds as any}
                        onItemClick={onItemClick}
                        onItemActivate={onItemActivate}
                        editMode={editMode}
                        onClearFocus={onClearFocus}

                    />
                ) : (
                    <WatchListNormalView
                        key={itemId}
                        data={item}
                        index={index}
                        isDraggedOver={draggedOverItem?.id === item.id}
                        watchlistId={selectedWatchlistId}
                        addOrRemoveWatchlistDataItem={handleWatchlistItemAddOrDelete}
                        subscribeSecurity={subscribeOrderBook}
                        handleDragOver={handleDragOver}
                        handleDragEnd={handleDragEnd}
                        handleDragStart={handleDragStart}
                        selectedItemIds={selectedItemIds as any}
                        onItemClick={onItemClick}
                        onItemActivate={onItemActivate}
                        editMode={editMode}
                        onClearFocus={onClearFocus}
                    />
                );
            })}
        </>
    );
}
export default WatchListGroupedPanel    